import { useAuth } from "../lib/auth";
import { isPlatformDeveloper } from "../lib/permissions";
import { isNative } from "../lib/runtime";

// Repo path used to build the promote-to-production link. Kept as a
// constant instead of an env var because it's a public repo URL —
// nothing sensitive, and hardcoding avoids yet another VITE_* to
// forget setting.
const REPO = "bond240/ShiftManager";
const STAGING_BRANCH = "claude/law-enforcement-scheduler-cWezn";

// Sidebar-embedded channel switcher for the platform developer, on
// BOTH channels, so I can jump between preview and production during
// testing without hunting for the URL. Rendered directly under the
// "ShiftManager · build XXX" banner inside Layout's dark sidebar
// (it used to be a floating chip in the top-right corner that
// covered page content). Non-developers never see anything —
// isPlatformDeveloper() is the sole gate.
//
//   * On PREVIEW  → amber "Preview build" chip with a "Promote to
//                   production" button (opens a pre-filled PR on
//                   GitHub) and a "Live site" link back to prod.
//                   The link carries ?stable=1 so the prod auto-
//                   hop doesn't immediately fling us back here.
//   * On STABLE   → sky "Live production" chip with a "Switch to
//                   preview" button. The link goes to /preview/
//                   plainly — auto-hop is already on ?stable, but
//                   the developer clicked deliberately.
//
// The build SHA already shows in the sidebar header link above this
// component, so no need to repeat it here.
export function StagingBanner() {
  const me = useAuth((s) => s.user);
  if (!isPlatformDeveloper(me)) return null;
  // Never render on the native (Capacitor) iOS/iPadOS build. iOS
  // apps ship through TestFlight / App Store, not through the
  // /preview/ vs / channel split — so there's nothing to hop to.
  if (isNative) return null;

  if (__BUILD_CHANNEL__ === "preview") {
    // Opens GitHub's compare page pre-loaded into "create PR" mode
    // with title + body already filled in — one click to create,
    // one more to merge. Requires being signed into GitHub in the
    // current browser; a signed-out visit 404s (even for a public
    // repo) because create-PR is an authenticated action. Fall back
    // is to sign in and click again.
    const promoteUrl =
      `https://github.com/${REPO}/compare/main...${STAGING_BRANCH}`
      + `?quick_pull=1`
      + `&title=${encodeURIComponent("Promote preview to production")}`
      + `&body=${encodeURIComponent(
          `Promoting the preview build to production.\n\nBuild: ${__BUILD_SHA__} at ${__BUILD_AT__}`,
        )}`;
    return (
      <div
        className="px-4 py-2.5 border-b border-slate-800 bg-amber-950/40 print:hidden"
        role="status"
      >
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-300">
            Preview build
          </span>
        </div>
        <div className="flex gap-1">
          <a
            href={promoteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-center rounded bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 text-[11px] font-semibold"
            title="Open a promote-to-production PR on GitHub"
          >
            Promote →
          </a>
          <a
            href="https://pdshiftmanager.com/?stable=1"
            className="rounded border border-amber-500/60 text-amber-200 hover:bg-amber-900/40 px-2 py-1 text-[11px]"
            title="Switch to the production build (won't auto-hop back this session)"
          >
            Live
          </a>
        </div>
      </div>
    );
  }

  // Stable channel — symmetric affordance for the developer to jump
  // to the preview build. Clears the "smStayOnStable" session flag
  // as the link is clicked so the auto-hop can resume normally
  // afterward if the developer bounces back.
  return (
    <div
      className="px-4 py-2.5 border-b border-slate-800 bg-sky-950/40 print:hidden"
      role="status"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-400" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-300">
          Live production
        </span>
      </div>
      <a
        href="https://pdshiftmanager.com/preview/"
        onClick={() => {
          try { sessionStorage.removeItem("smStayOnStable"); } catch { /* private mode */ }
        }}
        className="block text-center rounded bg-sky-600 hover:bg-sky-700 text-white px-2 py-1 text-[11px] font-semibold"
        title="Open the preview build for testing"
      >
        Switch to preview →
      </a>
    </div>
  );
}
